<template>
  <div>
    <p class="has-text-weight-bold is-size-4 mb-1">{{ stepCopy.name }}</p>
    <p class="mb-6">{{ stepCopy.description }}</p>

    <section v-if="!loaded">
      <article class="media" v-for="ll in 3" :key="ll">
        <figure class="media-left">
          <p class="image is-64x64">
            <b-skeleton circle width="64px" height="64px"></b-skeleton>
          </p>
        </figure>
        <div class="media-content">
          <div class="content">
            <p>
              <b-skeleton active></b-skeleton>
              <b-skeleton height="80px"></b-skeleton>
            </p>
          </div>
          <nav class="level is-mobile">
            <div class="level-left">
              <a class="level-item">
                <span class="icon is-small">
                  <b-skeleton></b-skeleton>
                </span>
              </a>
              <a class="level-item">
                <span class="icon is-small">
                  <b-skeleton></b-skeleton>
                </span>
              </a>
            </div>
          </nav>
        </div>
      </article>
    </section>

    <validation-observer v-else ref="flowObserver">
      <div v-for="question in stepCopy.questions" :key="question.id">
        <!--Simple fields-->
        <transition name="fade" mode="in-out">
          <div
            v-if="
              question.fieldType === FLOW_QUESTION_FIELD_TYPE.SIMPLE_FIELD &&
              checkVisibility(question)
            "
          >
            <!-- Input with validation -->
            <BInputWithValidation
              @keyup.native="
                refreshMask($event, simpleFieldGetter(question.fieldId).mask)
              "
              v-if="
                simpleFieldGetter(question.fieldId).type ===
                  SIMPLE_FIELD_TYPE.INPUT &&
                (question.isRequired ||
                  simpleFieldGetter(question.fieldId).validation)
              "
              :rules="getRules(question)"
              :label="simpleFieldGetter(question.fieldId).label"
              label-position="on-border"
              :value="
                readAnswer(
                  simpleFieldGetter(question.fieldId).identifier,
                  simpleFieldGetter(question.fieldId).mask
                )
              "
              @blur="
                writeAnswer(
                  $event,
                  simpleFieldGetter(question.fieldId).identifier
                )
              "
            />

            <!-- Input without Validation -->
            <b-field
              v-if="
                simpleFieldGetter(question.fieldId).type ===
                  SIMPLE_FIELD_TYPE.INPUT &&
                !(
                  question.isRequired ||
                  simpleFieldGetter(question.fieldId).validation
                )
              "
              :label="simpleFieldGetter(question.fieldId).label"
              label-position="on-border"
              class="mb-6"
            >
              <b-input
                @keyup.native="
                  refreshMask($event, simpleFieldGetter(question.fieldId).mask)
                "
                :value="
                  readAnswer(
                    simpleFieldGetter(question.fieldId).identifier,
                    simpleFieldGetter(question.fieldId).mask
                  )
                "
                @blur="
                  writeAnswer(
                    $event.currentTarget.value,
                    simpleFieldGetter(question.fieldId).identifier
                  )
                "
              ></b-input>
            </b-field>

            <!--Select-->
            <BSelectWithValidation
              v-if="
                simpleFieldGetter(question.fieldId).type ===
                SIMPLE_FIELD_TYPE.SELECT
              "
              :rules="question.isRequired ? 'required' : ''"
              :label="simpleFieldGetter(question.fieldId).label"
              label-position="on-border"
              :value="
                readAnswer(simpleFieldGetter(question.fieldId).identifier)
              "
              @input="
                writeAnswer(
                  $event,
                  simpleFieldGetter(question.fieldId).identifier
                )
              "
              class="mb-6"
            >
              <option
                v-for="(iv, index) in simpleFieldGetter(question.fieldId)
                  .inputValues"
                :value="iv.value"
                :key="index"
              >
                {{ iv.text }}
              </option>
            </BSelectWithValidation>

            <!--Date picker-->
            <b-field
              v-if="
                simpleFieldGetter(question.fieldId).type ===
                SIMPLE_FIELD_TYPE.DATE_PICKER
              "
              :label="simpleFieldGetter(question.fieldId).label"
              label-position="on-border"
              class="mb-6"
            >
              <b-datepicker
                :value="
                  readAnswer(simpleFieldGetter(question.fieldId).identifier)
                    ? new Date(
                        readAnswer(
                          simpleFieldGetter(question.fieldId).identifier
                        )
                      )
                    : null
                "
                @input="
                  writeAnswer($event, {
                    identifier: simpleFieldGetter(question.fieldId).identifier,
                    date: true,
                  })
                "
                locale="hu-HU"
                icon-pack="fa"
                icon="calendar"
                trap-focus
                :mobile-native="false"
              >
              </b-datepicker>
            </b-field>

            <!--Radio-->
            <ApplicationFlowRadioQuestion
              v-if="
                simpleFieldGetter(question.fieldId).type ===
                SIMPLE_FIELD_TYPE.RADIO
              "
              :field="simpleFieldGetter(question.fieldId)"
              :value="
                readAnswer(simpleFieldGetter(question.fieldId).identifier)
              "
              @input="
                writeAnswer(
                  $event,
                  simpleFieldGetter(question.fieldId).identifier
                )
              "
            />

            <!--Checkbox-->
            <ApplicationFlowCheckboxQuestion
              v-if="
                simpleFieldGetter(question.fieldId).type ===
                SIMPLE_FIELD_TYPE.CHECKBOX
              "
              :field="simpleFieldGetter(question.fieldId)"
              :value="
                readAnswer(simpleFieldGetter(question.fieldId).identifier)
              "
              @input="
                writeAnswer(
                  $event,
                  simpleFieldGetter(question.fieldId).identifier
                )
              "
            />
          </div>
        </transition>

        <!--Statement prompt-->
        <div
          v-if="
            question.fieldType === FLOW_QUESTION_FIELD_TYPE.STATEMENT_PROMPT &&
            checkVisibility(question)
          "
          class="mb-6"
        >
          <p class="is-size-7 has-text-grey-icon">
            {{ statementPromptGetter(question.fieldId).name }}
          </p>

          <div class="statement-tile is-flex is-align-items-center">
            <template
              v-if="
                statementPromptGetter(question.fieldId).type ===
                STATEMENT_PROMPT_TYPE.DOCUMENT
              "
            >
              <span
                @click="downloadFile(statementPromptGetter(question.fieldId))"
                class="cursor-pointer"
              >
                <b-icon
                  icon-pack="fa"
                  icon="file-download"
                  size="is-medium"
                  type="is-blue"
                  class="mr-2"
                ></b-icon>
              </span>

              <StatementReaderModal
                :documentId="
                  statementPromptGetter(question.fieldId).attachedDocument
                "
                :isRequiredToRead="
                  statementPromptGetter(question.fieldId).isReadingObligatory
                "
                :isPublic="false"
                :label="statementPromptGetter(question.fieldId).label"
                @accept="setPromptOpen(question.fieldId)"
              />
            </template>

            <template
              v-if="
                statementPromptGetter(question.fieldId).type ===
                STATEMENT_PROMPT_TYPE.LINK
              "
            >
              <StatementLink
                v-if="
                  statementPromptGetter(question.fieldId).type ===
                  STATEMENT_PROMPT_TYPE.LINK
                "
                :link="statementPromptGetter(question.fieldId).link"
                :isRequiredToRead="
                  statementPromptGetter(question.fieldId).isReadingObligatory
                "
                @accept="setPromptOpen(question.fieldId)"
                :label="statementPromptGetter(question.fieldId).label"
              />
            </template>

            <template v-if="!statementPromptGetter(question.fieldId).type">
              <label class="mr-2">{{
                statementPromptGetter(question.fieldId).label
              }}</label>
            </template>

            <ValidationProvider>
              <b-field>
                <b-switch
                  class="ml-auto"
                  type="is-blue"
                  :disabled="
                    statementPromptGetter(question.fieldId)
                      .isReadingObligatory && !openedPrompts[question.fieldId]
                  "
                  :value="
                    readAnswer(
                      statementPromptGetter(question.fieldId).identifier
                    )
                  "
                  @input="
                    saveAcceptance(
                      $event,
                      statementPromptGetter(question.fieldId),
                      question.isRequired,
                      statementPromptGetter(question.fieldId).type,
                      statementPromptGetter(question.fieldId).link
                    )
                  "
                />
              </b-field>
            </ValidationProvider>
          </div>
        </div>

        <!--Document upload-->
        <div
          v-if="
            question.fieldType === FLOW_QUESTION_FIELD_TYPE.DOCUMENT_PROMPT &&
            checkVisibility(question)
          "
        >
          <BUploadWithValidation
            :rules="
              question.isRequired &&
              !getFlowQuestionAnswer(
                documentPromptGetter(question.fieldId).identifier
              )
                ? 'required'
                : ''
            "
            :label="documentPromptGetter(question.fieldId).label"
            :prompt="documentPromptGetter(question.fieldId)"
            label-position="on-border"
            @input="
              writeAnswer($event, {
                identifier: documentPromptGetter(question.fieldId).identifier,
                file: true,
              })
            "
            @documentUploadStart="documentUploadStart"
            @documentUploadFinish="
              documentUploadFinish(documentPromptGetter(question.fieldId).id)
            "
            :disabled="documentUploading"
          />
          <br />
          <ApplicationFlowDocumentPromptDownloader
            :prompt-id="documentPromptGetter(question.fieldId).id"
            ref="documentPromptDownloaders"
          >
          </ApplicationFlowDocumentPromptDownloader>
        </div>

        <!-- Self Onboarding Special Field -->

        <SelfOnboardingSpecialField
          v-if="
            question.fieldType === FLOW_QUESTION_FIELD_TYPE.SPECIAL_FIELD &&
            question.fieldSubtype === SPECIAL_FIELD_TYPE.SELF_ONBOARDING
          "
          :specialFieldContent="specialFieldGetter(question.fieldId).content"
          :required="question.isRequired"
        />

        <!-- Branding Special Field -->

        <BrandingSpecialField
          v-if="
            question.fieldType === FLOW_QUESTION_FIELD_TYPE.SPECIAL_FIELD &&
            question.fieldSubtype === SPECIAL_FIELD_TYPE.MERCHANT_BRANDING
          "
          :specialFieldContent="specialFieldGetter(question.fieldId).content"
          :required="question.isRequired"
        />

        <!-- Doc Generate Special Field -->

        <DocGenerateSpecialField
          v-if="
            question.fieldType === FLOW_QUESTION_FIELD_TYPE.SPECIAL_FIELD &&
            question.fieldSubtype ===
              SPECIAL_FIELD_TYPE.DOC_GENERATE_AND_DOWNLOAD
          "
          :specialFieldContent="specialFieldGetter(question.fieldId).content"
          :questionConfigId="question.id"
          :fieldId="question.fieldId"
        />

        <!--Read only field-->
        <div
          v-if="
            question.fieldType === FLOW_QUESTION_FIELD_TYPE.READONLY_FIELD &&
            checkVisibility(question)
          "
          class="mb-6"
        >
          <p class="is-size-7 has-text-grey-icon">
            {{ readOnlyFieldGetter(question.fieldId).label }}
          </p>
          <p>{{ readOnlyFieldGetter(question.fieldId).description }}</p>
        </div>
      </div>
    </validation-observer>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { cloneDeep } from "lodash";
import {
  CONDITIONAL_FIELD_OPERATOR,
  FLOW_QUESTION_FIELD_TYPE,
  SPECIAL_FIELD_TYPE,
  SIMPLE_FIELD_TYPE,
  STATEMENT_PROMPT_TYPE,
} from "../../utils/const";
import { formatDate, maskValue } from "../../utils/util";
import Vue from "vue";
import FileSaver from "file-saver";
import eventBus, { EVENTS } from "@/utils/eventBus";

export default {
  name: "ApplicationFlowStep",
  components: {
    BInputWithValidation: () =>
      import("@/components/input/BInputWithValidation.vue"),
    ApplicationFlowDocumentPromptDownloader: () =>
      import(
        "@/components/application/ApplicationFlowDocumentPromptDownloader.vue"
      ),
    BSelectWithValidation: () =>
      import("@/components/input/BSelectWithValidation.vue"),
    BUploadWithValidation: () =>
      import("@/components/input/BUploadWithValidation.vue"),
    ApplicationFlowRadioQuestion: () =>
      import("@/components/application/ApplicationFlowRadioQuestion.vue"),
    ApplicationFlowCheckboxQuestion: () =>
      import("@/components/application/ApplicationFlowCheckboxQuestion.vue"),
    StatementReaderModal: () =>
      import("@/components/statement/StatementReaderModal.vue"),
    StatementLink: () => import("@/components/statement/StatementLink.vue"),
    SelfOnboardingSpecialField: () =>
      import("@/components/self-onboarding/SelfOnboardingSpecialField.vue"),
    BrandingSpecialField: () =>
      import("@/components/self-onboarding/BrandingSpecialField.vue"),
    DocGenerateSpecialField: () =>
      import("@/components/self-onboarding/DocGenerateSpecialField.vue"),
  },
  props: {
    step: {
      type: Object,
      required: true,
    },
    applicationId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      stepCopy: null,
      loaded: false,
      documentUploading: false,
      FLOW_QUESTION_FIELD_TYPE,
      SIMPLE_FIELD_TYPE,
      SPECIAL_FIELD_TYPE,
      STATEMENT_PROMPT_TYPE,
      openedPrompts: {},
    };
  },
  computed: {
    ...mapGetters({
      simpleFieldGetter: "simpleFieldStore/get",
      documentPromptGetter: "documentPromptStore/get",
      statementPromptGetter: "statementPromptStore/get",
      readOnlyFieldGetter: "readOnlyFieldStore/get",
      specialFieldGetter: "specialFieldStore/get",
      getFlowQuestionAnswer: "application/getFlowQuestionAnswer",
      fileGetter: "drawer/getFile",
    }),
  },
  created() {
    eventBus.$on(EVENTS.FLOW_VALIDATION, this.validate);
  },
  destroyed() {
    eventBus.$off(EVENTS.FLOW_VALIDATION);
  },
  watch: {
    step: {
      deep: true,
      immediate: true,
      handler: async function () {
        this.loaded = false;
        this.stepCopy = cloneDeep(this.step);
        this.stepCopy.questions.sort((a, b) => a.rowIndex - b.rowIndex);

        const promises = [];
        this.stepCopy.questions.forEach((question) => {
          if (question.fieldType === FLOW_QUESTION_FIELD_TYPE.SIMPLE_FIELD) {
            promises.push(this.getSimpleField(question.fieldId));
          } else if (
            question.fieldType === FLOW_QUESTION_FIELD_TYPE.DOCUMENT_PROMPT
          ) {
            promises.push(this.getDocumentPrompt(question.fieldId));
          } else if (
            question.fieldType === FLOW_QUESTION_FIELD_TYPE.STATEMENT_PROMPT
          ) {
            promises.push(this.getStatementPrompt(question.fieldId));
          } else if (
            question.fieldType === FLOW_QUESTION_FIELD_TYPE.READONLY_FIELD
          ) {
            promises.push(this.getReadOnlyField(question.fieldId));
          } else if (
            question.fieldType === FLOW_QUESTION_FIELD_TYPE.SPECIAL_FIELD
          ) {
            promises.push(this.getSpecialField(question.fieldId));
          }
        });

        await Promise.all(promises);

        // Set already accepted statements
        this.stepCopy.questions.forEach((question) => {
          if (
            question.fieldType === FLOW_QUESTION_FIELD_TYPE.STATEMENT_PROMPT
          ) {
            const answer = this.readAnswer(
              this.statementPromptGetter(question.fieldId).identifier
            );

            if (answer) {
              this.setPromptOpen(question.fieldId);
            }
          }
        });

        for (const question of this.stepCopy.questions) {
          if (question.isConditional && question.conditions) {
            for (const condition of question.conditions) {
              if (!this.simpleFieldGetter(condition.conditionalFieldId)) {
                await this.getSimpleField(condition.conditionalFieldId);
              }
            }
          }
        }

        this.loaded = true;
      },
    },
  },
  methods: {
    ...mapActions({
      getSimpleField: "simpleFieldStore/fetchOne",
      getDocumentPrompt: "documentPromptStore/fetchOne",
      getReadOnlyField: "readOnlyFieldStore/fetchOne",
      getStatementPrompt: "statementPromptStore/fetchOne",
      getSpecialField: "specialFieldStore/fetchOne",
      saveDynamicData: "application/saveDynamicData",
      download: "drawer/download",
    }),
    documentUploadStart() {
      this.documentUploading = true;
    },
    documentUploadFinish(promptId) {
      setTimeout(() => {
        this.documentUploading = false;

        let documentPromptDownloaders = this.$refs.documentPromptDownloaders;
        let documentPromptDownloader = documentPromptDownloaders.find(
          (documentPromptDownloader) =>
            documentPromptDownloader.promptId === promptId
        );

        if (documentPromptDownloader) {
          documentPromptDownloader.loadDocuments();
        }
      }, 2000);
    },
    async validate() {
      let flowObserver = this.$refs.flowObserver;
      if (flowObserver) {
        await flowObserver.validate();
      }
    },
    setPromptOpen(promptId) {
      Vue.set(this.openedPrompts, promptId, true);
    },
    refreshMask(event, mask) {
      let value = event.target.value;
      let maskedValue = maskValue(mask, value);
      event.target.value = maskedValue;
    },
    writeAnswer(value, identifier) {
      if (typeof identifier !== "string" && identifier.date) {
        value = formatDate(value);
        identifier = identifier.identifier;
      }
      if (typeof identifier !== "string" && identifier.file) {
        value = true;
        identifier = identifier.identifier;
      }
      this.saveDynamicData({
        key: identifier,
        value: value,
      });
    },
    getRules(question) {
      let rules = { required: question.isRequired };
      let regex = this.simpleFieldGetter(question.fieldId).validation;
      if (regex) {
        rules["regex"] = regex;
      }
      return rules;
    },
    readAnswer(identifier, mask) {
      return maskValue(
        mask,
        this.$store.getters["application/getFlowQuestionAnswer"](identifier)
      );
    },
    async saveAcceptance(checked, statement, isRequired, type, link) {
      try {
        let request = {
          statementId: statement.id,
          statementIdentifier: statement.identifier,
          statementLabel: statement.label,
          isRequired: !!isRequired,
          documentId:
            type === STATEMENT_PROMPT_TYPE.DOCUMENT
              ? statement.attachedDocument
              : null,
          link: type === STATEMENT_PROMPT_TYPE.LINK ? link : null,
          isAccepted: checked,
          layoutType: "FLOW",
          flowIdentifier: this.stepCopy.name,
          applicationId: this.applicationId,
        };
        await this.$store.dispatch("statements/acceptStatement", request);
        this.writeAnswer(checked, statement.identifier);
      } catch (ex) {
        console.error("Statement Acceptance failed!");
      }
    },
    checkVisibility(question) {
      if (question.hidden) {
        return false;
      }
      if (!question.isConditional) {
        return true;
      } else {
        let visible;
        for (const condition of question.conditions) {
          let index = question.conditions.indexOf(condition);
          let evaluate = this.evaluateCondition(condition);
          if (index === 0) {
            visible = evaluate;
          } else {
            let operator =
              question.conditions[index - 1].conditionalFieldOperator;
            if (CONDITIONAL_FIELD_OPERATOR.ENUM.AND === operator) {
              visible = visible && evaluate;
            } else if (CONDITIONAL_FIELD_OPERATOR.ENUM.OR === operator) {
              visible = visible || evaluate;
            }
          }
        }
        return visible;
      }
    },
    evaluateCondition(condition) {
      let dependency = this.simpleFieldGetter(condition.conditionalFieldId);
      const answer = this.getFlowQuestionAnswer(dependency.identifier);

      // The checkbox answer is an array, not a single string
      if (dependency.type === SIMPLE_FIELD_TYPE.CHECKBOX) {
        if (answer) {
          let answerObject = JSON.parse(answer);
          return answerObject.includes(condition.conditionalFieldAnswer);
        } else {
          return false;
        }
      } else {
        return answer === condition.conditionalFieldAnswer;
      }
    },
    async downloadFile({ attachedDocument: id, name }) {
      await this.download(id);
      let blob = this.fileGetter(id);
      FileSaver.saveAs(blob, name);
    },
  },
};
</script>

<style scoped lang="scss">
.statement-tile {
  background: #f3f5f9;
  border-radius: 8px;
  text-align: center;
  padding: 24px;
}
</style>
